import { useState, useEffect, lazy, Suspense } from "react";
import { Link } from "wouter";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { PageMeta } from "@/components/seo/PageMeta";
import { organizationSchema, websiteSchema } from "@/lib/seo/jsonLd";
// Wave 45 — gsap + useScrollReveal removed from the homepage. The
// hero stagger is now a CSS keyframe (`heroEnterIn` below); there
// were no `[data-reveal]` markers on this page so useScrollReveal()
// was a no-op that still pulled gsap + ScrollTrigger (~80 KiB) into
// the marketing critical path. Other pages that DO use data-reveal
// keep the hook — this change is scoped to home.tsx.
// WorkflowDemo removed in round 8 — covered by AutomationDiagram.
import { mkt, typography } from "@/theme/tokens";
import DeferUntilNear from "@/components/marketing/DeferUntilNear";
/* Removed: TrustMarquee (used fabricated customer logos — dishonest trust
 * signal that would fail any "google these companies" sniff test) and the
 * triple-row animated HeroTradeDivider (visual noise; the "Built for"
 * cycling badge already names the trade). */
import { SurfaceSection } from "@/components/marketing/SurfaceSection";
import TrustSection from "@/components/marketing/TrustSection";
import CTASection from "@/components/marketing/CTASection";
import BenefitsGrid from "@/components/marketing/showcase/BenefitsGrid";
import { Check, ArrowRight, Star } from "lucide-react";
import { PRIMARY_CTA } from "@/site/cta";

/* ─── Below-the-fold heavy components — lazy-loaded ───
 *
 * These render below the hero and add ~hundreds of KB to the JS bundle.
 * React.lazy + Suspense defers their parse/eval until the chunk arrives.
 * Suspense fallbacks reserve approximate heights to minimise CLS during
 * the late-load.
 */
const CapabilitiesShowcase = lazy(() => import("@/components/marketing/CapabilitiesShowcase"));
const StickyStackCards = lazy(() => import("@/components/marketing/StickyStackCards"));
const ServiceStackTimeline = lazy(() => import("@/components/marketing/ServiceStackTimeline"));
const ReviewsSection = lazy(() => import("@/components/home/ReviewsSection"));
const AutomationDiagram = lazy(() => import("@/components/marketing/AutomationDiagram"));
/* QuoteIQ-inspired sections placed directly under the hero — lazy + deferred
 * like the other below-fold sections so they never compete with the hero LCP. */
const ToolShowcase = lazy(() => import("@/components/marketing/ToolShowcase"));
const CostCompare = lazy(() => import("@/components/marketing/CostCompare"));
/* Wave 69 — Self-Service Drag-Drop interactive element. Lazy + deferred
 * because it sits below the IntegrationsTrustStrip (still part of the
 * hero zone visually, but well below the fold on mobile). The component
 * is ~30KB minified and the IntersectionObserver wrap keeps it off the
 * critical path until the visitor scrolls toward it. */
const SelfServiceDragDrop = lazy(() => import("@/components/marketing/SelfServiceDragDrop"));
/* "Compete with the big brands" — interactive claim-the-map coverage section
 * (Option E from /mockups/compete), wired in directly above the Self-Service
 * card per Alex. Named export → wrap to a default for React.lazy. */
const CompeteCoverageMap = lazy(() =>
  import("@/components/marketing/competeVariants").then((m) => ({ default: m.CompeteCoverageMap })),
);

/* Fallback boxes — reserve typical rendered heights so the page doesn't
 * jump when the lazy chunk arrives. Tuned for desktop; mobile heights are
 * smaller but the CLS impact is the same proportion. */
const lazyFallback = (minHeight: number) => (
  <div aria-hidden="true" style={{ minHeight, background: "transparent" }} />
);
/* ── Hero email capture form ── */
function HeroEmailCapture() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErrorMsg("Please enter a valid email address");
      setStatus("error");
      return;
    }
    setStatus("loading");
    try {
      const res = await fetch("/api/demo-leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          trade: "general",
          source_tool: "hero_audit",
          source_page: "homepage",
        }),
      });
      if (!res.ok) throw new Error("Failed");
      setStatus("success");
    } catch {
      setErrorMsg("Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="hero-enter" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 24px", borderRadius: 14, background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.25)", maxWidth: 480, margin: "0 auto" }}>
        <Check size={16} color="#10B981" strokeWidth={2.5} />
        <span style={{ fontSize: 14, fontWeight: 600, color: "#10B981", fontFamily: typography.fontFamily }}>
          We'll send your free website audit shortly.
        </span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="hero-enter hero-email-form" style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", maxWidth: 520, margin: "0 auto" }}>
      <input
        type="email"
        placeholder="Enter your email for a free website audit"
        aria-label="Email address for free website audit"
        value={email}
        onChange={(e) => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
        style={{
          flex: 1, minWidth: 220, padding: "12px 16px", borderRadius: 12,
          border: status === "error" ? "1px solid rgba(239,68,68,0.5)" : "1px solid rgba(255,255,255,0.12)",
          background: "rgba(255,255,255,0.06)", color: mkt.text, fontSize: 14,
          fontFamily: typography.fontFamily, outline: "none",
          transition: "border-color 0.2s",
        }}
        onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(13,60,252,0.4)"; }}
        onBlur={(e) => { e.currentTarget.style.borderColor = status === "error" ? "rgba(239,68,68,0.5)" : "rgba(255,255,255,0.12)"; }}
      />
      <button
        type="submit"
        disabled={status === "loading"}
        style={{
          padding: "12px 24px", borderRadius: 10, border: "none",
          background: mkt.ctaBg, color: mkt.ctaText, fontSize: 14, fontWeight: 500,
          fontFamily: typography.fontFamily, cursor: status === "loading" ? "wait" : "pointer",
          transition: "background 0.2s", whiteSpace: "nowrap",
          opacity: status === "loading" ? 0.7 : 1,
        }}
      >
        {status === "loading" ? "Sending..." : "Get My Free Audit"}
      </button>
      {status === "error" && (
        <div style={{ width: "100%", fontSize: 12, color: "#EF4444", marginTop: 4, textAlign: "center" }}>
          {errorMsg}
        </div>
      )}
    </form>
  );
}

const RESPONSIVE_CSS = `
  .mkt-btn-primary:focus-visible, .mkt-btn-ghost:focus-visible {
    outline: 2px solid ${mkt.accent};
    outline-offset: 2px;
  }
  /* Wave 45 — hero stagger as CSS, replacing the gsap.fromTo() that
   * pulled the gsap vendor chunk into the homepage critical path.
   * Same visual cadence (translateY 36 → 0, opacity 0 → 1, ~0.78s,
   * 0.13s stagger, brand cubic-bezier). */
  @keyframes heroEnterIn {
    from { opacity: 0; transform: translateY(36px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .hero-enter {
    opacity: 0;
    transform: translateY(36px);
    animation: heroEnterIn 0.78s cubic-bezier(0.526, 0.007, 0, 0.989) 0.1s forwards;
  }
  .hero-enter:nth-of-type(1) { animation-delay: 0.10s; }
  .hero-enter:nth-of-type(2) { animation-delay: 0.23s; }
  .hero-enter:nth-of-type(3) { animation-delay: 0.36s; }
  .hero-enter:nth-of-type(4) { animation-delay: 0.49s; }
  .hero-enter:nth-of-type(5) { animation-delay: 0.62s; }
  .hero-enter:nth-of-type(6) { animation-delay: 0.75s; }
  @media (prefers-reduced-motion: reduce) {
    .hero-enter {
      animation: none;
      opacity: 1;
      transform: none;
    }
  }
  @keyframes heroPillIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .hero-pill {
    opacity: 0;
    animation: heroPillIn 0.4s cubic-bezier(0.4,0,0.2,1) forwards;
  }
  .hero-pill:nth-child(1) { animation-delay: 0.15s; }
  .hero-pill:nth-child(2) { animation-delay: 0.3s; }
  .hero-pill:nth-child(3) { animation-delay: 0.45s; }
  .hero-pill:nth-child(4) { animation-delay: 0.6s; }
  /* Direction C: 2-column hero — copy left, animated product preview right.
   * Collapses to single centered column below 960px so the preview stacks
   * underneath the copy on tablets and phones. */
  .hero-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 48px;
    align-items: center;
    text-align: left;
  }
  .hero-copy-col { text-align: left; }
  .hero-preview-col { display: flex; justify-content: flex-end; }
  @media (max-width: 960px) {
    .hero-grid { grid-template-columns: 1fr; gap: 36px; text-align: center; }
    .hero-copy-col { text-align: center; }
    .hero-preview-col { justify-content: center; }
    .hero-cta-row { justify-content: center; }
  }
  .hero-cta-row { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; }
  /* Mobile (≤640px): keep the two hero CTAs on one row instead of stacking.
   * flex-wrap: nowrap + flex: 1 1 0 + min-width: 0 lets both buttons share
   * the row width without overflow on 360–414px viewports. Padding is
   * tightened to keep the ≥44px tap target while shrinking horizontally. */
  @media (max-width: 640px) {
    .hero-cta-row {
      flex-wrap: nowrap !important;
      gap: 10px !important;
      width: 100%;
      max-width: 100%;
    }
    .hero-cta-row > .hero-cta-primary,
    .hero-cta-row > .hero-cta-secondary {
      flex: 1 1 0;
      min-width: 0;
      padding: 13px 14px;
      justify-content: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 13px;
    }
    .hero-cta-row > .hero-cta-primary > span,
    .hero-cta-row > .hero-cta-secondary > span {
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }
  }
  /* DOSS pattern: cream off-white primary CTA, no blue glow.
   * Hover is a subtle color deepening (no halo, no transform stack).
   * font-weight 500 matches DOSS's "confident not shouty" tone. */
  .hero-cta-primary {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 14px 22px; border-radius: 10px;
    background: ${mkt.ctaBg}; color: ${mkt.ctaText};
    font-size: 14px; font-weight: 500; letter-spacing: 0.01em;
    text-decoration: none; border: none; cursor: pointer;
    transition: background 0.2s ease, transform 0.15s ease;
  }
  .hero-cta-primary:hover { background: ${mkt.ctaBgHover}; transform: translateY(-1px); }
  .hero-cta-primary:active { transform: translateY(1px); }
  .hero-cta-primary:focus-visible { outline: 2px solid ${mkt.focusRing}; outline-offset: 2px; }
  .hero-cta-secondary {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 14px 22px; border-radius: 10px;
    background: transparent;
    color: ${mkt.ctaSecondaryText}; border: 1px solid ${mkt.ctaSecondaryBorder};
    font-size: 14px; font-weight: 500;
    text-decoration: none; cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;
  }
  .hero-cta-secondary:hover { background: ${mkt.ctaSecondaryBgHover}; border-color: ${mkt.ctaSecondaryBorderHover}; }
  .hero-cta-secondary:focus-visible { outline: 2px solid ${mkt.focusRing}; outline-offset: 2px; }
  .hero-cta-note { font-size: 12px; color: ${mkt.textMuted}; }

  /* ─── W-HERO warm-canvas variants (Alex variant 04) ───
   * Scoped to the homepage hero only. Primary is the deep brand blue on
   * cream with a subtle inset highlight + drop shadow. Secondary is a
   * ghost outline that fills to slate-100 on hover. */
  .hero-cta-primary-warm {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 14px 22px; border-radius: 10px;
    /* White-on-blue per DESIGN-SYSTEM (night-audit P-A: mkt.buttonText is
     * the deprecated cream-button ink #1E1E1E — dark-on-blue read 2.44:1). */
    background: ${mkt.accent}; color: ${mkt.onDark};
    font-size: 14px; font-weight: 600; letter-spacing: 0.01em;
    text-decoration: none; cursor: pointer;
    border: 0.5px solid rgba(15,23,42,0.06);
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.18),
      0 4px 14px rgba(13,60,252,0.28),
      0 1px 2px rgba(15,23,42,0.06);
    transition: background 0.18s ease, transform 0.15s ease, box-shadow 0.18s ease;
  }
  .hero-cta-primary-warm:hover {
    background: ${mkt.accentHover};
    transform: translateY(-1px);
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.22),
      0 6px 20px rgba(13,60,252,0.34),
      0 2px 4px rgba(15,23,42,0.08);
  }
  .hero-cta-primary-warm:active { transform: translateY(0); }
  .hero-cta-primary-warm:focus-visible { outline: 2px solid ${mkt.accent}; outline-offset: 3px; }
  .hero-cta-secondary-warm {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 14px 22px; border-radius: 10px;
    background: transparent;
    color: #334155;
    border: 1.5px solid ${mkt.warmHairlineStrong};
    font-size: 14px; font-weight: 600;
    text-decoration: none; cursor: pointer;
    transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
  }
  .hero-cta-secondary-warm:hover {
    background: rgba(15,23,42,0.04);
    border-color: rgba(15,23,42,0.32);
    color: ${mkt.onWarm};
  }
  .hero-cta-secondary-warm:focus-visible { outline: 2px solid ${mkt.accent}; outline-offset: 3px; }
  .hero-cta-note-warm { font-size: 12px; color: ${mkt.onWarmMuted}; }
  /* Warm-variant CTA row keeps the same mobile collapse rules. */
  .hero-cta-row > .hero-cta-primary-warm,
  .hero-cta-row > .hero-cta-secondary-warm { box-sizing: border-box; }
  @media (max-width: 640px) {
    /* Full-width stacked CTAs per the hero blueprint. The previous
     * nowrap + flex:1 1 0 + ellipsis row clipped the longer primary label
     * ("Build your free calculator") on ~375px viewports. Stacking gives
     * each button the full width so both labels read in full. */
    .hero-cta-row {
      flex-direction: column !important;
      align-items: stretch !important;
    }
    .hero-cta-row > .hero-cta-primary-warm,
    .hero-cta-row > .hero-cta-secondary-warm {
      flex: none;
      width: 100%;
      padding: 14px 18px;
      justify-content: center;
      white-space: nowrap;
      font-size: 14px;
    }
  }
  /* ─── Video hero (Alex: looped bg video + feathered gradient perimeter) ───
   * The video fills the rounded zone; the feather melts its edges into the
   * dark surround (no hard corners) and darkens for text readability. Colors
   * use rgb()/rgba() only — the hardcoded-colors guard flags literal hex. */
  .hero-bg-video {
    position: absolute; inset: 0; width: 100%; height: 100%;
    object-fit: cover; z-index: 0;
  }
  .hero-video-feather {
    position: absolute; inset: 0; z-index: 1; pointer-events: none;
    background:
      linear-gradient(180deg, rgba(8,11,18,0.45) 0%, rgba(8,11,18,0.18) 34%, rgba(8,11,18,0.30) 68%, rgba(8,11,18,0.74) 100%),
      radial-gradient(135% 130% at 50% 42%, rgba(8,11,18,0) 46%, rgba(8,11,18,0.52) 76%, rgba(8,11,18,0.90) 100%);
    box-shadow: inset 0 0 160px 60px rgba(8,11,18,0.85);
  }
  @media (prefers-reduced-motion: reduce) { .hero-bg-video { display: none; } }
  .hero-video-content { padding-top: 6px; }
  .hero-video-eyebrow {
    margin: 0 0 18px; font-size: 12px; font-weight: 700; letter-spacing: 0.18em;
    text-transform: uppercase; color: rgba(255,255,255,0.74);
    display: inline-block; padding: 7px 16px; border-radius: 999px;
    background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15);
    -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
  }
  .hero-video-h1 {
    font-size: clamp(34px, 6vw, 62px); font-weight: 800; line-height: 1.04;
    letter-spacing: -0.025em; color: rgba(255,255,255,1);
    text-shadow: 0 2px 26px rgba(0,0,0,0.5);
  }
  .hero-video-hl { color: rgb(150,180,255); }
  .hero-video-sub {
    max-width: 650px; margin: 20px auto 0; font-size: clamp(15px, 1.7vw, 18px);
    line-height: 1.62; font-weight: 450; color: rgba(255,255,255,0.88);
    text-shadow: 0 1px 14px rgba(0,0,0,0.45);
  }
  .hero-video-cta { justify-content: center; margin-top: 28px; }
  .hero-cta-secondary-onvid {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 14px 22px; border-radius: 10px;
    background: rgba(255,255,255,0.10); color: rgba(255,255,255,1);
    border: 1px solid rgba(255,255,255,0.30); font-size: 14px; font-weight: 600;
    text-decoration: none; cursor: pointer;
    -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
    transition: background 0.18s ease, border-color 0.18s ease;
  }
  .hero-cta-secondary-onvid:hover { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.48); }
  .hero-cta-secondary-onvid:focus-visible { outline: 2px solid rgba(255,255,255,1); outline-offset: 3px; }
  /* Alex: white border on hover for the primary CTA (inset ring → no layout shift). */
  .hero-video-cta .hero-cta-primary-warm:hover {
    box-shadow: inset 0 0 0 1.5px rgba(255,255,255,0.92),
      0 6px 20px rgba(13,60,252,0.34), 0 2px 4px rgba(15,23,42,0.10);
  }
  .hero-video-note { margin-top: 14px; font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.70); }
  .hero-video-trust {
    margin-top: 16px; display: inline-flex; align-items: center; gap: 10px;
    padding: 9px 18px; border-radius: 999px;
    background: rgba(8,11,18,0.42); border: 1px solid rgba(255,255,255,0.16);
    -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
    font-size: 13px; color: rgba(255,255,255,0.90);
  }
  .hero-video-stars { color: rgb(255,197,61); letter-spacing: 1px; font-size: 13px; }
  .hero-video-trust-txt strong { color: rgba(255,255,255,1); font-weight: 800; }
  @media (max-width: 640px) {
    .hero-video-cta { flex-direction: column !important; align-items: stretch !important; width: 100%; }
    .hero-video-cta > .hero-cta-primary-warm,
    .hero-video-cta > .hero-cta-secondary-onvid { width: 100%; justify-content: center; }
    .hero-video-trust { flex-wrap: wrap; justify-content: center; text-align: center; }
  }

  /* Warm hero subtle canvas-noise overlay — drawn over the cream base.
   * Uses a tiny inline SVG to avoid an extra asset round-trip. */
  .hero-warm-noise {
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: 0.55;
    mix-blend-mode: multiply;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.35  0 0 0 0 0.32  0 0 0 0 0.26  0 0 0 0.18 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    background-size: 160px 160px;
    z-index: 0;
  }
  /* Top + bottom hairlines that "frame" the warm hero section. */
  .hero-warm-hairline {
    position: absolute;
    left: 8%; right: 8%;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${mkt.warmHairlineStrong} 18%, ${mkt.warmHairlineStrong} 82%, transparent);
    opacity: 0.55;
    pointer-events: none;
    z-index: 1;
  }
  /* Bottom transition: 80px gradient strip fading cream → dark slate
   * so the hand-off to the IntegrationsTrustStrip on dark is buttery,
   * not a hard color edge. */
  .hero-warm-fade {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 96px;
    background: linear-gradient(to bottom, rgba(243,237,223,0) 0%, rgba(40,46,49,0.18) 55%, ${mkt.darkBg} 100%);
    pointer-events: none;
    z-index: 2;
  }
  /* Scoped headline polish for warm hero — micro text-shadow for that
   * "premium printed page" feel without smudging the type. */
  .hero-warm-headline {
    text-shadow: 0 1px 2px rgba(15,23,42,0.04);
  }
  @media (max-width: 768px) {
    .hero-section-responsive { padding: 96px 20px 28px !important; }
    .hero-subtext { font-size: 16px !important; }
  }
  @media (max-width: 640px) {
    .hero-section-responsive { padding: 92px 18px 24px !important; }
    .hero-subtext { margin-bottom: 18px !important; }
    .hero-email-form { flex-direction: column !important; }
    .hero-email-form input { min-width: 0 !important; }
    .hero-stats-strip { gap: 16px !important; }
    .hero-pills-grid { grid-template-columns: 1fr 1fr !important; gap: 6px !important; }
    .hero-pill {
      height: 34px !important;
      padding: 8px 12px !important;
      font-size: 11px !important;
      gap: 6px !important;
      border-radius: 12px !important;
    }
    .hero-pill svg { width: 13px !important; height: 13px !important; }
    .hero-pill-label-full { display: none !important; }
    .hero-pill-label-short { display: inline !important; }
    .hero-subtext { font-size: 15px !important; margin-bottom: 28px !important; }
    .hero-cta-row { gap: 8px !important; }
    .hero-cta-row .cta-arrow-btn__text { font-size: 10px !important; }
    .built-for-chip { height: 28px !important; padding: 4px 10px !important; gap: 6px !important; }
    .built-for-chip .bf-label { font-size: 11px !important; }
    .built-for-chip .bf-window { height: 16px !important; width: 130px !important; }
    .built-for-chip .bf-window * { font-size: 11px !important; }
  }
  @media (min-width: 641px) {
    .hero-pill-label-full { display: inline !important; }
    .hero-pill-label-short { display: none !important; }
  }
  @media (max-width: 640px) {
    .hero-safe-zone {
      width: 92% !important;
      height: 60% !important;
    }
    /* Hide the animated grid-glow squares on mobile. On the narrow cream
     * hero they crowd/overlap the headline and read as stray broken blocks
     * rather than a subtle backdrop (they're tuned for the wide desktop
     * canvas). Desktop keeps the effect. */
    .hero-grid-glow { display: none !important; }
  }
  /* Hero shell — Cloudflare-style framed container */
  .hero-first-screen-zone {
    min-height: 640px;
  }
  @media (min-width: 768px) {
    .hero-first-screen-zone {
      min-height: 720px;
    }
  }
  /* Backdrop responsive gaps */
  @media (max-width: 768px) {
    .hero-shell-backdrop {
      padding: 6px 4px 0 !important;
    }
    .hero-first-screen-zone {
      border-radius: 20px !important;
    }
  }
  @media (max-width: 430px) {
    .hero-shell-backdrop {
      padding: 6px 3px 0 !important;
    }
    .hero-first-screen-zone {
      border-radius: 18px !important;
    }
  }
  /* Tablet: hero zone + 150px divider = 100svh */
  @media (max-width: 1024px) {
    .hero-first-screen-zone {
      min-height: calc(100svh - 150px);
    }
  }
  /* Mobile: hero zone + 130px divider = 100svh */
  @media (max-width: 640px) {
    .hero-first-screen-zone {
      min-height: calc(100svh - 130px);
    }
  }
  /* Common mobile viewport class (around 390x844): keep first fold on hero + trade divider */
  @media (max-width: 430px) {
    .hero-first-screen-zone {
      min-height: calc(100svh - 72px);
      padding-bottom: clamp(56px, 9svh, 92px);
    }
  }
`;

export default function HomePage() {
  // Title + meta tags handled by <PageMeta> below.
  //
  // Wave 45 — hero entrance stagger moved to CSS (`heroEnterIn` keyframe
  // + `.hero-enter:nth-child(...)` animation-delay rules in
  // RESPONSIVE_CSS). Same visual cadence (y:36 → 0, opacity 0 → 1,
  // ~0.78s, 0.13s stagger, brand ease) without pulling gsap into the
  // homepage bundle. `prefers-reduced-motion` respected via the
  // `@media (prefers-reduced-motion: reduce)` block at the bottom of
  // the keyframe — animations get duration:0 so the targets land in
  // their final state immediately.

  return (
    <MarketingLayout>
      <PageMeta
        title="Quote calculators + AI receptionist for trades"
        description="WeFixTrades gives plumbers, electricians, HVAC, roofers, and trade contractors embeddable quote calculators, 24/7 AI lead handling, Google Maps SEO, and reputation tools — get 3x more leads from the work you already do."
        canonical="/"
        ogType="website"
        keywords={[
          "trades software",
          "quote calculator",
          "plumber crm",
          "hvac software",
          "electrician software",
          "ai receptionist",
          "google maps seo",
        ]}
        jsonLd={[organizationSchema(), websiteSchema()]}
      />
      <style>{RESPONSIVE_CSS}</style>

      {/* Outer page background behind hero shell — kept dark slate so the
       * cream hero reads as a "warm canvas island" framed by the rest of
       * the site palette (Alex variant 04 / W-HERO). */}
      <div className="hero-shell-backdrop" style={{ background: `radial-gradient(120% 78% at 14% 0%, rgba(13,60,252,0.10) 0%, rgba(13,60,252,0.03) 38%, transparent 66%), radial-gradient(92% 70% at 86% 6%, rgba(124,58,237,0.09) 0%, rgba(6,182,212,0.05) 42%, transparent 72%), ${mkt.darkBg}`, padding: "6px 6px 0", position: "relative" as const, zIndex: 1 }}>
      {/* Warm-canvas hero zone — cream base + low-opacity canvas noise.
       * IntegrationsTrustStrip lives OUTSIDE this zone (on dark) so the
       * cream doesn't have to deal with white-text integration logos. */}
      <div className="hero-first-screen-zone hero-video-zone" style={{ position: "relative", background: mkt.darkBg, overflow: "hidden", display: "flex", flexDirection: "column", width: "100%", borderRadius: 24, border: "1px solid rgba(255,255,255,0.06)", boxShadow: "0 24px 70px rgba(3,7,18,0.50), inset 0 1px 0 rgba(255,255,255,0.06)" }}>
        {/* Looped trades background video — muted/auto/loop, poster for fast LCP. */}
        <video className="hero-bg-video" autoPlay muted loop playsInline preload="metadata" poster="/hero/trades-hero-poster.jpg" aria-hidden="true">
          <source src="/hero/trades-hero.mp4" type="video/mp4" />
        </video>
        {/* Feathered gradient shade — melts the video edges into the dark
         * surround (no hard corners) and darkens for text readability. */}
        <div aria-hidden="true" className="hero-video-feather" />


      <section
        data-testid="hero-section"
        className="hero-section-responsive"
        style={{
          background: "transparent",
          /* compression: hero padding trimmed (was 132/96). Still leaves
           * ~110px top so the navbar (88px) has air. */
          padding: "104px 28px 40px",
          marginTop: -8,
          position: "relative",
        }}
      >

        {/* Centre text-legibility scrim — soft dark ellipse directly behind
         * the copy so the centered headline always clears the moving video. */}
        <div
          aria-hidden="true"
          className="hero-safe-zone"
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            maxWidth: 1040,
            height: "70%",
            background: `radial-gradient(ellipse at center, rgba(8,11,18,0.48) 0%, rgba(8,11,18,0.30) 40%, rgba(8,11,18,0.10) 65%, transparent 82%)`,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />

        <style>{`
          @keyframes wf_underline_beam {
            0% { transform: translateX(-30%); opacity: 0; }
            8% { opacity: 0.95; }
            70% { opacity: 0.95; }
            100% { transform: translateX(130%); opacity: 0; }
          }
          .wf-underline {
            position: relative;
            display: inline-block;
          }
          .wf-underline::before {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            bottom: -10px;
            height: 3px;
            border-radius: 999px;
            background: rgba(13,60,252, 0.22);
            z-index: 0;
          }
          .wf-underline::after {
            content: "";
            position: absolute;
            left: 0;
            bottom: -10px;
            height: 3px;
            width: 34%;
            border-radius: 999px;
            background: linear-gradient(
              90deg,
              rgba(13,60,252,0) 0%,
              rgba(13,60,252,0.85) 45%,
              rgba(13,60,252,0) 100%
            );
            animation: wf_underline_beam 6.25s ease-in-out infinite;
            z-index: 1;
          }
          @keyframes wf_shimmer_sweep {
            0%   { transform: translateX(-140%) skewX(-18deg); opacity: 0; }
            8%   { opacity: 0.85; }
            32%  { opacity: 0.85; }
            40%  { opacity: 0; }
            100% { transform: translateX(240%) skewX(-18deg); opacity: 0; }
          }
          .wf-cta-shimmer {
            position: relative;
            overflow: hidden;
            isolation: isolate;
          }
          .wf-cta-shimmer::after {
            content: "";
            position: absolute;
            inset: 0;
            z-index: 1;
            pointer-events: none;
            background: linear-gradient(
              90deg,
              rgba(255,255,255,0) 0%,
              rgba(255,255,255,0.06) 38%,
              rgba(255,255,255,0.35) 50%,
              rgba(255,255,255,0.06) 62%,
              rgba(255,255,255,0) 100%
            );
            animation: wf_shimmer_sweep 4.6s ease-in-out infinite;
          }
          .wf-cta-shimmer > * {
            position: relative;
            z-index: 2;
          }
          @media (prefers-reduced-motion: reduce) {
            .wf-cta-shimmer::after {
              animation: none !important;
              opacity: 0 !important;
            }
          }
        `}</style>

        {/* Centered hero content over the looped video — QuoteIQ-style, our
         * palette/fonts/buttons. "AI" is deliberately de-emphasised (out of the
         * headline; one light mention in the subhead) so old-school trades
         * aren't put off. */}
        <div className="hero-video-content hero-enter" style={{ maxWidth: 860, margin: "0 auto", position: "relative", zIndex: 2, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <p className="hero-video-eyebrow">Built for home service pros</p>

          <h1 data-testid="hero-headline" className="hero-video-h1" style={{ margin: 0, fontFamily: typography.fontFamily }}>
            The <span className="hero-video-hl">#1 growth engine</span><br />for home service pros.
          </h1>

          <p data-testid="hero-subtext" className="hero-video-sub" style={{ fontFamily: typography.fontFamily }}>
            More calls, more booked jobs, more 5-star reviews — your quotes, bookings and follow-ups handled for you, so you stay on the tools. Smart automation, quietly AI-supported. Nothing new to learn.
          </p>

          <div className="hero-cta-row hero-video-cta hero-enter">
            <Link href="/wizard" className="hero-cta-primary-warm wf-cta-shimmer" data-testid="hero-cta-primary">
              <span>{PRIMARY_CTA.label}</span>
              <ArrowRight size={16} strokeWidth={2.5} />
            </Link>
            <Link href="/demo" className="hero-cta-secondary-onvid" data-testid="hero-cta-secondary">
              <span>Watch demo</span>
            </Link>
          </div>

          <div className="hero-video-note" data-testid="hero-cta-note">
            Free plan — no card needed · Cancel anytime · Setup in under 10 minutes
          </div>

          {/* Trust pill directly under the CTAs (QuoteIQ pattern) — honest
           * numbers reused from TrustSection (240+ businesses, 4.9★). */}
          <div className="hero-video-trust" data-testid="hero-trust-strip">
            <span className="hero-video-stars" aria-hidden="true">★★★★★</span>
            <span className="hero-video-trust-txt"><strong>4.9</strong> · Trusted by 240+ trade businesses</span>
          </div>
        </div>

      </section>

      </div>{/* end hero video zone */}

      {/* QuoteIQ-inspired "Every tool you need" tap-to-expand showcase — placed
       * directly under the hero per Alex. Lazy + deferred like the sections below. */}
      <DeferUntilNear minHeight={720}>
        <Suspense fallback={lazyFallback(720)}>
          <ToolShowcase />
        </Suspense>
      </DeferUntilNear>

      {/* Cost-comparison calculator — WeFixTrades vs the competitor stack. */}
      <DeferUntilNear minHeight={720}>
        <Suspense fallback={lazyFallback(720)}>
          <CostCompare />
        </Suspense>
      </DeferUntilNear>

      {/* "Compete with the big brands" — interactive coverage map. Replaces the
       * old static "big chains" comparison table (IntegrationsTrustStrip) per
       * Alex. Deferred + lazy like the other below-fold sections. */}
      <DeferUntilNear minHeight={620} rootMargin="500px">
        <Suspense fallback={lazyFallback(620)}>
          <CompeteCoverageMap />
        </Suspense>
      </DeferUntilNear>

      {/* Wave 69 — Self-Service Drag-Drop. First interactive proof-of-
       * product moment after the hero. The visitor watches an existing
       * pricing doc become a live calculator + reserve flow without any
       * setup. Deferred so its ~30KB doesn't compete with the hero LCP. */}
      <DeferUntilNear minHeight={680}>
        <Suspense fallback={lazyFallback(680)}>
          <SelfServiceDragDrop />
        </Suspense>
      </DeferUntilNear>
      </div>{/* end hero shell backdrop */}
      {/* HeroTradeDivider removed in the C-direction premium rewrite —
       * the cycling "Built for: <trade>" badge above the headline already
       * names the trade. */}

      {/* Free-audit lead magnet — demoted out of the hero so the primary
       * CTAs (Start free / See demo) own the first fold. Kept as a quiet
       * secondary capture for visitors who aren't ready to sign up but
       * will trade an email for a Google audit. */}
      <section
        data-testid="hero-audit-section"
        style={{
          background: mkt.darkBg,
          /* compression: trimmed (was 56). */
          padding: "36px 24px",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
          <div
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px", borderRadius: 999,
              background: "rgba(13,60,252,0.08)",
              border: "1px solid rgba(13,60,252,0.18)",
              fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
              textTransform: "uppercase", color: mkt.accent,
              fontFamily: "'DM Mono', monospace",
              marginBottom: 16,
            }}
          >
            <Star size={12} strokeWidth={2.5} /> Free for trade businesses
          </div>
          <h2
            style={{
              fontSize: "clamp(22px, 3vw, 30px)",
              fontWeight: 700,
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
              margin: 0,
              marginBottom: 10,
              color: mkt.text,
              fontFamily: typography.fontFamily,
            }}
          >
            Not ready to sign up? Get a free website &amp; Google audit instead.
          </h2>
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.5,
              color: mkt.textMuted,
              maxWidth: 560,
              margin: "0 auto 22px",
              fontFamily: typography.fontFamily,
            }}
          >
            We'll send a one-page report: what's costing you leads, where your Google ranking is leaking, and the 3 fastest fixes. No call required.
          </p>
          <HeroEmailCapture />
        </div>
      </section>

      {/* "What you get" — premium-white at-a-glance overview of the system, so a
          visitor gets the 5-second "what is all this?" before the detailed,
          interactive showcases below prove each piece. Moved here from /pricing
          (it belongs in the marketing funnel, not on the money page) and the
          old solid-blue tray is now premium white. Lightweight (pure CSS/SVG) so
          it renders inline — no lazy/vendor chunk. */}
      <SurfaceSection className="py-10">
        <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 20px" }}>
          <h2 style={{ fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 800, color: mkt.onDark, letterSpacing: "-0.02em", margin: "0 0 8px", textAlign: "center" }}>
            Everything working together
          </h2>
          <p style={{ fontSize: 15, color: mkt.onDarkMuted, lineHeight: 1.5, margin: "0 auto 28px", textAlign: "center", maxWidth: 560 }}>
            One system that answers every lead, prices the job, climbs Google, and protects your reputation — so the work runs while you're on the tools.
          </p>
          <BenefitsGrid maxWidth={1080} />
        </div>
      </SurfaceSection>

      {/* Three product showcase types covering all 12 products — each lazy-
          loaded to keep the initial home.tsx bundle small.
          Wave 45 — wrapped in <DeferUntilNear> so React.lazy() doesn't
          fetch their vendor chunks (motion/charts/globe) during the
          hero paint. The Suspense fallback heights are mirrored as the
          DeferUntilNear minHeight to keep CLS at 0. */}
      <DeferUntilNear minHeight={560}>
        <Suspense fallback={lazyFallback(560)}>
          <CapabilitiesShowcase />        {/* 4 money-makers */}
        </Suspense>
      </DeferUntilNear>
      <DeferUntilNear minHeight={520}>
        <Suspense fallback={lazyFallback(520)}>
          <StickyStackCards />            {/* 4 growth tools */}
        </Suspense>
      </DeferUntilNear>
      <DeferUntilNear minHeight={520}>
        <Suspense fallback={lazyFallback(520)}>
          <ServiceStackTimeline />        {/* 4 done-for-you */}
        </Suspense>
      </DeferUntilNear>
      {/* Removed legacy sections (PillarAnimation, FeatureCards, ServiceCards,
          WorkflowDemo) — they duplicated the 3-type story above and broke the
          V7 visual cohesion as the user scrolled. AutomationDiagram remains as
          the interactive "How it works" deep-dive. */}
      <SurfaceSection overlap className="py-4">
        <DeferUntilNear minHeight={480}>
          <Suspense fallback={lazyFallback(480)}>
            <ReviewsSection />
          </Suspense>
        </DeferUntilNear>
      </SurfaceSection>
      <DeferUntilNear minHeight={560}>
        <Suspense fallback={lazyFallback(560)}>
          <AutomationDiagram />
        </Suspense>
      </DeferUntilNear>
      <TrustSection />
      <CTASection />
    </MarketingLayout>
  );
}
